import type { ComponentProps } from "react";
import { use } from "react";
import { LuLock } from "react-icons/lu";

import { DIALOG, setDialog } from "@/components/app/dialogs/dialog-provider";
import { InfoTooltip } from "@/components/shared/info-tooltip";
import { Button } from "@/components/ui/button";
import { UserContext } from "@/contexts/user-context";
import { cn } from "@/utils/lib";

import { useEditorContext } from "../editor-context";

export const EditorActionGated = ({
  className,
  ...props
}: ComponentProps<typeof Button>) => {
  const { setEditorContext, subscriptionTier } = useEditorContext();
  const { user } = use(UserContext);
  const isGatedPost = !!subscriptionTier;

  return (
    <InfoTooltip
      mobile={false}
      text="Gated posts are encrypted and require a subscription or payment to access, while previews are visible to entice readers."
    >
      <Button
        variant={isGatedPost ? "outlinePrimary" : "outline"}
        size="icon"
        aria-label="Toggle visibility"
        className={cn(
          "relative rounded-full text-muted",
          isGatedPost
            ? `
              text-primary
              hover:border-muted-foreground hover:text-muted-foreground
            `
            : "hover:border-muted hover:text-muted",
          className,
        )}
        onClick={() => {
          if (!user?.publicKey) {
            return;
          }

          setDialog(DIALOG.GatedContentOptions, {
            open: true,
            historyMode: "entry",
            props: {
              creatorPublicKey: user.publicKey,
              currentOptions: subscriptionTier,
              onResetOptions: () => {
                setEditorContext((previous) => ({
                  subscriptionTier: null,
                  lastFocusedField:
                    previous.lastFocusedField === "gatedBody"
                      ? "body"
                      : previous.lastFocusedField,
                }));
              },
              onSaveOptions: (nextSubscriptionTier) => {
                setEditorContext((previous) => ({
                  subscriptionTier: nextSubscriptionTier,
                  lastFocusedField:
                    previous.subscriptionTier && previous.lastFocusedField
                      ? previous.lastFocusedField
                      : "gatedBody",
                }));
              },
            },
          });
        }}
        {...props}
      >
        <LuLock />
      </Button>
    </InfoTooltip>
  );
};
